import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';

import { useCallbackRef } from '@/hooks/useCallbackRef';

type ScopeKeyHandler = (event: KeyboardEvent) => void;

const FeedKeyScopeContext =
  createContext<RefObject<ScopeKeyHandler | null> | null>(null);

// The part of a page whose keys go to its open card, wherever focus is inside it. Keys outside it, such as on the
// nav rail, stay with what has focus. Where scopes nest, a key goes only to the scope nearest the focus
export function FeedKeyScope({ children }: { children: ReactNode }) {
  const handler = useRef<ScopeKeyHandler | null>(null);
  return (
    <FeedKeyScopeContext.Provider value={handler}>
      <div
        data-feed-key-scope
        className="contents"
        onKeyDownCapture={(event) => {
          // A key from a portal, such as a popover, has no scope around it in the DOM and belongs to this one
          const nearest =
            event.target instanceof Element
              ? event.target.closest('[data-feed-key-scope]')
              : null;
          if (!nearest || nearest === event.currentTarget)
            handler.current?.(event);
        }}
      >
        {children}
      </div>
    </FeedKeyScopeContext.Provider>
  );
}

// While `active`, the card answers the keys of the scope around it. Returns whether it does, since a card with no scope
// answers only the keys inside its own frame
export function useFeedKeyScope(
  active: boolean,
  onKeyDown: ScopeKeyHandler
): boolean {
  const scope = useContext(FeedKeyScopeContext);
  const handler = useCallbackRef(onKeyDown);
  useEffect(() => {
    if (!active || !scope) return;
    scope.current = handler;
    return () => {
      if (scope.current === handler) scope.current = null;
    };
  }, [active, scope, handler]);
  return active && scope !== null;
}
